import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useQuery } from '@apollo/client';
import { WidgetHeader } from '../../common/WidgetHeader';
import { useWidgetPopout } from '~/hooks/useWidgetPopout';
import { Divider } from 'primereact/divider';
import { NoDataAvailableTemplate } from '../../common/NoDataAvailableTemplate';
import { IndicatorDot } from '../../misc/widget/indicatorDot';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { ProgressBar } from 'primereact/progressbar';
import { AllocationAnalysisDialog } from './AllocationAnalysisDialog';
import { AUMConfirmationDialog } from '../../common/AUMConfirmationDialog';
import {
  endpointPortfolioAnalysisPOST,
  PortfolioAnalysisRequest,
} from '~/api/def/endpoints/portfolioAnalysisEndpoint';
import { toastAtom } from '~/atoms/toastAtom';
import {
  selectWidgetAIAtom,
  selectedTickerFilterAtom,
  portfolioIdAtom,
  latestSnapshotIdAtom,
  triggerHardRefetchAtom,
} from '~/atoms/dashboardAtom';
import type { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import { HoverTooltip } from '../../common/HoverTooltip';
import { Link } from '@tanstack/react-router';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { useSnapshotVersionCheck } from '~/hooks/useSnapshotVersionCheck';
import { SnapshotVersionWarning } from '../../common/SnapshotVersionWarning';
import { usePortfolioAnalysis } from '~/hooks/usePortfolioAnalysis';
import { getAnalysisDisplayState } from '~/utils/analysisStatusDisplay';
import { GET_EQALLOCATION_DATA } from '~/graphql/queries/eqallocation';
// 15 min

interface CSVAllocationData {
  isin: string;
  current_allocation: number;
  sector: string;
}

export interface AllocationData {
  full_ticker: string;
  name: string;
  currentAllocation: number;
  proposedAllocation: number;
  sector: string;
  changeType: string;
}

export type AllocationWidgetProps = {
  data: AllocationData[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  handleRemoveWidget?: (widgetId: string) => void;
  widgetId: string;
  title?: string;
  portfolioSummary: {
    aum: number;
    totalPositions: number;
  };
  snapshotId?: string;
  aiRecommendations?: {
    action: string;
    full_ticker: string;
    currentAllocation: string;
    proposedAllocation: string;
    reason: string;
    confidence: 'High' | 'Medium' | 'Low';
  }[];
};

export const AllocationWidget: React.FC<AllocationWidgetProps> = ({
  data,
  isLoading,
  error,
  refetch,
  handleRemoveWidget,
  widgetId,
  title = 'Portfolio Allocation',
  portfolioSummary,
  snapshotId,
  aiRecommendations,
}) => {
  const [analysisDialogVisible, setAnalysisDialogVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCSVUploading, setIsCSVUploading] = useState(false);
  const [
    excludedInstrumentsDialogVisible,
    setExcludedInstrumentsDialogVisible,
  ] = useState(false);
  const [excludedInstruments, setExcludedInstruments] = useState<string[]>([]);
  const [toastCurrent] = useAtom<Toast | null>(toastAtom);
  const setSelectWidgetAI = useSetAtom(selectWidgetAIAtom);
  const [selectedTickerFilter, setSelectedTickerFilter] = useAtom(
    selectedTickerFilterAtom,
  );
  const portfolioId = useAtomValue(portfolioIdAtom);
  const setLatestSnapshotId = useSetAtom(latestSnapshotIdAtom);
  const setTriggerHardRefetch = useSetAtom(triggerHardRefetchAtom);

  // Snapshot version check
  const {
    showWarning,
    latestSnapshotId,
    checkIfOutdated,
    dismissWarning,
    refreshToLatest,
  } = useSnapshotVersionCheck();

  // Portfolio analysis hook - handles all analysis submission, polling, and state
  const {
    isAnalyzing,
    analysisProgress,
    analysisStatus,
    analysisStatusValue,
    aumDialogVisible,
    portfolioData,
    currentAUM,
    currency,
    isUpdatingAUM,
    handleReAnalyse,
    handleAUMConfirm,
    setAumDialogVisible,
    resetAnalysisState,
  } = usePortfolioAnalysis({
    snapshotId,
    onAnalysisComplete: () => {
      refetch();
    },
  });

  const hasValidSnapshotId =
    typeof snapshotId === 'string' && snapshotId.length > 0;
  // Check if reference universe is uploaded — use network-only to always get fresh data
  // (matches the upload page's PlainTableWidgetWrapper fetch policy)
  const { data: allocationData } = useQuery(GET_EQALLOCATION_DATA, {
    variables: { snapshotId: hasValidSnapshotId ? snapshotId : '' },
    skip: !hasValidSnapshotId,
    fetchPolicy: 'network-only',
  });

  const hasReferenceUniverse = useMemo(() => {
    return (
      allocationData?.universe &&
      allocationData.universe.length > 0 &&
      !!allocationData.universe[0]?.allocation?.id
    );
  }, [allocationData]);

  const analyseTooltipContent = useMemo(() => {
    if (hasReferenceUniverse) return null;
    if (typeof portfolioId === 'string' && portfolioId.length > 0) {
      return (
        <span>
          Reference universe is required to run portfolio analysis. Upload it
          from the{' '}
          <Link
            to="/portfolio/$portfolioId/upload"
            params={{ portfolioId }}
            style={{
              color: '#60a5fa',
              fontWeight: 600,
              textDecoration: 'underline',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            Upload page
          </Link>
          .
        </span>
      );
    }
    return 'Reference universe is required to run portfolio analysis. You can upload it from the Upload page (portfolio → Upload in the sidebar).';
  }, [hasReferenceUniverse, portfolioId]);

  // Add popout functionality
  const handlePopout = useWidgetPopout({
    widgetId,
    widgetComponent: AllocationWidget,
    title: title || 'Portfolio Allocation',
    defaultWidth: 1400,
    defaultHeight: 900,
    widgetProps: {
      data,
      isLoading,
      error,
      refetch,
      handleRemoveWidget,
      widgetId,
      title,
      portfolioSummary,
      snapshotId,
      aiRecommendations,
    },
  });

  // Wrapper for WidgetHeader compatibility
  const handlePopoutWrapper = useCallback(
    (widgetId: string) => {
      handlePopout?.(widgetId);
    },
    [handlePopout],
  );

  // Check snapshot version when component mounts or data changes
  useEffect(() => {
    if (snapshotId && latestSnapshotId) {
      checkIfOutdated();
    }
  }, [snapshotId, latestSnapshotId, checkIfOutdated]);

  const removeWidget = useCallback(
    () => handleRemoveWidget?.(widgetId),
    [widgetId, handleRemoveWidget],
  );

  // Utility function to show toast messages
  const showToast = useCallback(
    (
      severity: 'success' | 'error' | 'warn',
      summary: string,
      detail: string,
      life: number = 3000,
    ) => {
      if (toastCurrent) {
        toastCurrent.show({ severity, summary, detail, life });
      }
    },
    [toastCurrent],
  );

  const processCSVData = useCallback(
    async (
      csvData: CSVAllocationData[],
      description: string,
    ): Promise<void> => {
      try {
        setIsCSVUploading(true);
        // Filter out rows with empty ISINs and transform CSV data to match API requirements
        const allocations = csvData
          .filter((row) => row.isin && row.isin.trim() !== '')
          .map((row) => {
            const allocation: {
              isin: string;
              current_allocation: number;
              sector_override?: string;
            } = {
              isin: row.isin,
              current_allocation: row.current_allocation,
            };

            if (row.sector) {
              allocation.sector_override =
                row.sector === 'c' ? 'Cybersecurity' : 'Defense';
            }

            return allocation;
          });

        if (allocations.length === 0) {
          return;
        }

        if (!portfolioId) {
          showToast(
            'error',
            'Analysis Failed',
            'No portfolio ID available for analysis.',
          );
          return;
        }

        // Prepare API request
        const requestData: PortfolioAnalysisRequest = {
          portfolio_id: portfolioId,
          allocations: allocations,
          description:
            description.trim() ||
            `Portfolio allocation analysis from CSV upload - ${new Date().toISOString()}`,
          snapshot_type: 'manual',
        };

        // Submit to portfolio analysis API
        const response = await endpointPortfolioAnalysisPOST(requestData);
        if (
          response &&
          response.excluded_instruments &&
          response.excluded_instruments.length > 0
        ) {
          setExcludedInstruments(response.excluded_instruments);
          setExcludedInstrumentsDialogVisible(true);
        }

        if (response && response.snapshot_id) {
          setLatestSnapshotId(response.snapshot_id);
          setTriggerHardRefetch((prev) => prev + 1);
          showToast(
            'success',
            'Snapshot Submitted',
            'Portfolio snapshot submitted successfully!',
            5000,
          );
        }
        setIsCSVUploading(false);
      } catch (error: unknown) {
        setIsCSVUploading(false);
        throw error;
      }
    },
    [portfolioId, setLatestSnapshotId, setTriggerHardRefetch, showToast],
  );

  const chartData = useMemo(() => {
    return data || [];
  }, [data]);

  const noData = useMemo(() => {
    return !chartData.length;
  }, [chartData]);

  // Apply ticker filter when it changes
  useEffect(() => {
    if (selectedTickerFilter) {
      setSearchTerm(selectedTickerFilter);
      // Clear the filter after applying it
      setSelectedTickerFilter(null);
    }
  }, [selectedTickerFilter, setSelectedTickerFilter]);

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return data;

    const searchLower = searchTerm.toLowerCase();
    return data.filter(
      (item) =>
        item.full_ticker.toLowerCase().includes(searchLower) ||
        item.name.toLowerCase().includes(searchLower),
    );
  }, [data, searchTerm]);

  // Utility function to determine indicator color based on AI recommendations
  const getIndicatorColor = useCallback(
    (ticker: string) => {
      // Check if this ticker has AI recommendations
      const aiRec = aiRecommendations?.find(
        (rec) => rec.full_ticker === ticker,
      );
      if (aiRec) {
        // Use AI recommendation action to determine color
        switch (aiRec.action.toLowerCase()) {
          case 'increase':
            return 'green'; // Green for buy/increase
          case 'sell':
          case 'reduce':
            return 'red'; // Red for sell/decrease
          case 'hold':
          case 'maintain':
            return 'blue'; // Red for hold/maintain (neutral)
        }
      }
    },
    [aiRecommendations],
  );

  if (isLoading) {
    return (
      <div className="p-3 h-full">
        <Skeleton className="h-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget || undefined}
          refetch={() => refetch()}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopoutWrapper}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="Something went wrong."
          isError
        />
      </div>
    );
  }

  if (!snapshotId) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget || undefined}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopoutWrapper}
          widgetId={widgetId}
          refetch={() => refetch()}
          children={
            <div className="flex align-items-center gap-2">
              <Button
                size="small"
                className="text-black flex gap-2 ml-2"
                loading={isCSVUploading}
                onClick={() => setAnalysisDialogVisible(true)}
                style={{
                  padding: '0.4375rem 0.65625rem',
                }}
              >
                New
              </Button>
              <HoverTooltip
                content={analyseTooltipContent}
                position="bottom"
                disabled={hasReferenceUniverse}
              >
                <span className="analyse-btn-wrap inline-block">
                  <Button
                    size="small"
                    className={`text-black ${!hasReferenceUniverse ? 'p-button-disabled' : ''}`}
                    onClick={() => {
                      checkIfOutdated();
                      handleReAnalyse();
                    }}
                    disabled={!hasReferenceUniverse || isAnalyzing}
                    style={{
                      whiteSpace: 'nowrap',
                      padding: '0.4375rem 0.65625rem',
                      opacity: isAnalyzing
                        ? 0.5
                        : !hasReferenceUniverse
                          ? 0.6
                          : 1,
                      ...(!hasReferenceUniverse && {
                        cursor: 'not-allowed',
                        pointerEvents: 'none',
                      }),
                    }}
                  >
                    Analyse
                  </Button>
                </span>
              </HoverTooltip>
            </div>
          }
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="No allocation data available."
          subTitle="Click 'New' to upload your first portfolio snapshot"
        />
        <AllocationAnalysisDialog
          visible={analysisDialogVisible}
          onHide={() => setAnalysisDialogVisible(false)}
          forceCurrentHoldings={true}
          onDataProcessed={(csvData, description) =>
            processCSVData(csvData, description)
          }
        />

        <ConfirmDialog
          visible={excludedInstrumentsDialogVisible}
          onHide={() => setExcludedInstrumentsDialogVisible(false)}
          message={
            <div>
              <p>The following ISINs are not supported in the pilot version:</p>
              <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                {excludedInstruments.map((isin, index) => (
                  <div key={index} style={{ marginBottom: '0.5rem' }}>
                    {isin}
                  </div>
                ))}
              </div>
            </div>
          }
          header="Excluded ISINs"
          acceptLabel="OK"
          accept={() => setExcludedInstrumentsDialogVisible(false)}
          acceptClassName="p-button-primary"
          className="p-confirm-dialog-no-reject"
        />
      </div>
    );
  }

  if (noData) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget || undefined}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopoutWrapper}
          widgetId={widgetId}
          refetch={() => refetch()}
          children={
            <div className="flex align-items-center gap-2">
              <Button
                size="small"
                className="text-black flex gap-2 ml-2"
                loading={isCSVUploading}
                onClick={() => setAnalysisDialogVisible(true)}
                style={{
                  padding: '0.4375rem 0.65625rem',
                }}
              >
                New
              </Button>
              <HoverTooltip
                content={analyseTooltipContent}
                position="bottom"
                disabled={hasReferenceUniverse}
              >
                <span className="analyse-btn-wrap inline-block">
                  <Button
                    size="small"
                    className={`text-black ${!hasReferenceUniverse ? 'p-button-disabled' : ''}`}
                    onClick={() => {
                      checkIfOutdated();
                      handleReAnalyse();
                    }}
                    disabled={!hasReferenceUniverse || isAnalyzing}
                    style={{
                      whiteSpace: 'nowrap',
                      padding: '0.4375rem 0.65625rem',
                      opacity: isAnalyzing
                        ? 0.5
                        : !hasReferenceUniverse
                          ? 0.6
                          : 1,
                      ...(!hasReferenceUniverse && {
                        cursor: 'not-allowed',
                        pointerEvents: 'none',
                      }),
                    }}
                  >
                    Analyse
                  </Button>
                </span>
              </HoverTooltip>
            </div>
          }
        />
        <NoDataAvailableTemplate
          icon="pi pi-inbox"
          title="No allocation data available."
          subTitle="Click 'New' to upload your first portfolio snapshot"
        />

        <AllocationAnalysisDialog
          visible={analysisDialogVisible}
          forceCurrentHoldings={true}
          onHide={() => setAnalysisDialogVisible(false)}
          onDataProcessed={(csvData, description) =>
            processCSVData(csvData, description)
          }
        />

        <ConfirmDialog
          visible={excludedInstrumentsDialogVisible}
          onHide={() => setExcludedInstrumentsDialogVisible(false)}
          message={
            <div>
              <p>The following ISINs are not supported in the pilot version:</p>
              <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                {excludedInstruments.map((isin, index) => (
                  <div key={index} style={{ marginBottom: '0.5rem' }}>
                    {isin}
                  </div>
                ))}
              </div>
            </div>
          }
          header="Excluded ISINs"
          acceptLabel="OK"
          accept={() => setExcludedInstrumentsDialogVisible(false)}
          acceptClassName="p-button-primary"
          className="p-confirm-dialog-no-reject"
        />
      </div>
    );
  }

  return (
    <div className="hover-container h-full flex flex-column" id={widgetId}>
      <WidgetHeader
        title={title}
        removeWidget={removeWidget || undefined}
        setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
        onPopout={handlePopoutWrapper}
        widgetId={widgetId}
        refetch={() => refetch()}
        children={
          <div className="flex align-items-center gap-2">
            <Button
              size="small"
              className="text-black flex gap-2 ml-2"
              loading={isCSVUploading}
              onClick={() => setAnalysisDialogVisible(true)}
              style={{
                padding: '0.4375rem 0.65625rem',
              }}
            >
              New
            </Button>
            <HoverTooltip
              content={analyseTooltipContent}
              position="bottom"
              disabled={hasReferenceUniverse}
            >
              <span className="analyse-btn-wrap inline-block">
                <Button
                  size="small"
                  className={`text-black ${!hasReferenceUniverse ? 'p-button-disabled' : ''}`}
                  onClick={() => {
                    checkIfOutdated();
                    handleReAnalyse();
                  }}
                  disabled={!hasReferenceUniverse || isAnalyzing}
                  style={{
                    whiteSpace: 'nowrap',
                    padding: '0.4375rem 0.65625rem',
                    opacity: isAnalyzing
                      ? 0.5
                      : !hasReferenceUniverse
                        ? 0.6
                        : 1,
                    ...(!hasReferenceUniverse && {
                      cursor: 'not-allowed',
                      pointerEvents: 'none',
                    }),
                  }}
                >
                  Analyse
                </Button>
              </span>
            </HoverTooltip>
          </div>
        }
      />

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Snapshot Version Warning */}
        {showWarning && (
          <div className="px-3 pt-3">
            <SnapshotVersionWarning
              onRefresh={async () => {
                const newSnapshotId = await refreshToLatest();
                if (newSnapshotId) {
                  setLatestSnapshotId(newSnapshotId);
                  setTriggerHardRefetch((prev) => prev + 1);
                }
              }}
              onDismiss={dismissWarning}
            />
          </div>
        )}
        <div className="p-3 h-full">
          {isAnalyzing &&
            (() => {
              const displayState = getAnalysisDisplayState({
                analysisProgress,
                analysisStatusValue,
              });
              return (
                <Card
                  className="mb-3"
                  style={{ backgroundColor: 'rgba(0, 0, 0, 0.1)' }}
                >
                  <div className="flex align-items-center justify-content-between mb-2">
                    <div className="flex align-items-center gap-2">
                      <i
                        className={displayState.icon}
                        style={{ fontSize: '1.2rem' }}
                      ></i>
                      <span
                        className={`font-semibold ${displayState.textColor}`}
                      >
                        {displayState.message === 'Analyzing'
                          ? 'Running Snapshot Analysis'
                          : `Snapshot ${displayState.message}`}
                      </span>
                    </div>
                    <div className="flex align-items-center gap-2">
                      <span
                        className="text-sm"
                        style={{ color: 'var(--bq-text-tertiary)' }}
                      >
                        {Math.round(analysisProgress)}%
                      </span>
                      {displayState.showDismiss && (
                        <i
                          className="pi pi-times cursor-pointer"
                          onClick={() => {
                            resetAnalysisState();
                          }}
                          style={{
                            fontSize: '1.2rem',
                            color: 'var(--bq-text-tertiary)',
                            transition: 'color 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color =
                              'var(--bq-text-secondary)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color =
                              'var(--bq-text-tertiary)';
                          }}
                        ></i>
                      )}
                    </div>
                  </div>
                  <ProgressBar
                    value={analysisProgress}
                    className="h-2rem"
                    color={displayState.progressBarColor}
                  />
                  <div
                    className="mt-2 text-sm"
                    style={{
                      color:
                        displayState.textColor === 'text-red-400'
                          ? 'var(--bq-color-negative)'
                          : 'var(--bq-text-tertiary)',
                    }}
                  >
                    {analysisStatus ||
                      'Processing portfolio data and generating analysis...'}
                  </div>
                </Card>
              );
            })()}
          <Card
            className="h-full"
            pt={{
              content: {
                className: 'p-0 m-0 h-full',
              },
              body: {
                className: 'h-full',
              },
            }}
          >
            <div
              className="pb-3 border-bottom-1"
              style={{ borderColor: 'var(--bq-border-muted)' }}
            >
              <div className="p-inputgroup flex-1">
                <span className="p-inputgroup-addon">
                  <i className="pi pi-search"></i>
                </span>
                <InputText
                  placeholder="Search by ticker or company name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <span
                    className="p-inputgroup-addon cursor-pointer"
                    onClick={() => setSearchTerm('')}
                    style={{
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        'var(--bq-surface-muted)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '';
                    }}
                  >
                    <i className="pi pi-times"></i>
                  </span>
                )}
              </div>
              {searchTerm && (
                <div
                  className="text-xs pt-2"
                  style={{ color: 'var(--bq-text-tertiary)' }}
                >
                  Showing {filteredData.length} of {data.length} allocations
                </div>
              )}
            </div>
            <Tooltip target=".ticker-tooltip" position="top" />
            <div
              className="overflow-y-auto mt-3"
              style={{ height: 'calc(100% - 10rem)' }}
            >
              {filteredData.length === 0 ? (
                <div
                  className="p-4 text-center"
                  style={{ color: 'var(--bq-text-tertiary)' }}
                >
                  {searchTerm.trim() ? (
                    <>
                      <i className="pi pi-search text-2xl mb-2 block"></i>
                      <p style={{ color: 'var(--bq-text-primary)' }}>
                        No companies found matching "{searchTerm}"
                      </p>
                      <p
                        className="text-sm mt-1"
                        style={{ color: 'var(--bq-text-tertiary)' }}
                      >
                        Try adjusting your search terms
                      </p>
                    </>
                  ) : (
                    <>
                      <i className="pi pi-inbox text-2xl mb-2 block"></i>
                      <p>No allocation data available</p>
                    </>
                  )}
                </div>
              ) : (
                filteredData.map((item: AllocationData, index: number) => (
                  <div
                    key={`${widgetId}-${item.full_ticker}-${index}-${item.currentAllocation}`}
                    className={`bq-allocation-item flex align-items-center justify-content-between px-2 py-1 border-round ${
                      index < filteredData.length - 1 ? 'mb-3' : ''
                    }`}
                    style={{
                      border: `2px solid ${
                        aiRecommendations?.some(
                          (rec) => rec.full_ticker === item.full_ticker,
                        )
                          ? 'var(--bq-color-info-strong)'
                          : 'var(--bq-border-muted)'
                      }`,
                    }}
                  >
                    <div className="flex flex-column w-full gap-2 py-2">
                      {/* Top line: Company Name and Sector */}
                      <div className="flex justify-content-between align-items-center">
                        <span
                          className="text-left"
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: 'var(--bq-text-secondary)',
                          }}
                        >
                          {item.name}
                        </span>
                        <span
                          className="text-sm"
                          style={{ color: 'var(--bq-text-secondary)' }}
                        >
                          {item.sector}
                        </span>
                      </div>

                      {/* Bottom line: Ticker, Allocation, and Indicator */}
                      <div className="flex justify-content-between align-items-center">
                        <div className="flex align-items-center gap-2">
                          <span
                            className="ticker-tooltip font-semibold"
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: 'var(--bq-color-info-strong)',
                            }}
                          >
                            {item.full_ticker}
                          </span>
                        </div>
                        <div className="flex align-items-center gap-2">
                          <span style={{ color: 'var(--bq-text-secondary)' }}>
                            {item.currentAllocation}%
                          </span>
                          <span style={{ color: 'var(--bq-text-secondary)' }}>
                            →
                          </span>
                          <span
                            className="px-3 py-1 border-round"
                            style={{
                              backgroundColor: 'var(--bq-surface-muted)',
                              color: 'var(--bq-text-primary)',
                            }}
                          >
                            {item.proposedAllocation}%
                          </span>
                          <IndicatorDot
                            color={getIndicatorColor(item.full_ticker)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <Divider />
            <div
              className="mt-4 space-y-2"
              style={{ color: 'var(--bq-text-tertiary)' }}
            >
              <div className="flex justify-content-between">
                <span>Instrument Universe:</span>
                <span>{portfolioSummary?.totalPositions}</span>
              </div>
              <div className="flex justify-content-between pt-2">
                <span>Total AUM:</span>
                <span>{portfolioSummary?.aum?.toLocaleString()}</span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <AllocationAnalysisDialog
        visible={analysisDialogVisible}
        forceCurrentHoldings={true}
        onHide={() => setAnalysisDialogVisible(false)}
        onDataProcessed={(csvData, description) =>
          processCSVData(csvData, description)
        }
      />

      <ConfirmDialog
        visible={excludedInstrumentsDialogVisible}
        onHide={() => setExcludedInstrumentsDialogVisible(false)}
        message={
          <div>
            <p>The following ISINs are not supported in the pilot version:</p>
            <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
              {excludedInstruments.map((isin, index) => (
                <div key={index} style={{ marginBottom: '0.5rem' }}>
                  {isin}
                </div>
              ))}
            </div>
          </div>
        }
        header="Excluded ISINs"
        acceptLabel="OK"
        accept={() => setExcludedInstrumentsDialogVisible(false)}
        acceptClassName="p-button-primary"
        className="p-confirm-dialog-no-reject"
      />

      <AUMConfirmationDialog
        visible={aumDialogVisible}
        onHide={() => setAumDialogVisible(false)}
        onConfirm={(aum) => handleAUMConfirm(aum, refetch)}
        snapshotAUM={portfolioSummary?.aum || 0}
        currency={currency}
        isLoading={isUpdatingAUM || isAnalyzing}
      />
    </div>
  );
};
